/**
 * Per-biome climate and atmosphere data (vanilla 1.21 values): base
 * temperature, whether it precipitates, sky / fog / water-fog colours and the
 * Nether's per-biome fog + ambient particle settings. Pure data + pure
 * functions, shared by the weather simulation and the renderer.
 *
 * Grass / foliage / water tints live in BIOMES (biomes.ts) and are blended by
 * biomeTint.ts; this file adds what those lack.
 */

import { Biome } from './biomes';
import { NetherBiome } from './nether';

const B = Biome;

/** Vanilla base temperature per biome. Index = Biome id. */
export const BIOME_TEMP: readonly number[] = (() => {
  const t: number[] = new Array(B.DeepDark + 1).fill(0.8);
  const set = (v: number, ...ids: Biome[]): void => { for (const i of ids) t[i] = v; };
  set(0.5, B.Ocean, B.DeepOcean, B.WarmOcean, B.LukewarmOcean, B.DeepLukewarmOcean, B.ColdOcean, B.DeepColdOcean,
    B.River, B.Meadow, B.CherryGrove, B.LushCaves);
  set(0, B.FrozenOcean, B.DeepFrozenOcean, B.FrozenRiver, B.SnowyPlains, B.IceSpikes);
  set(0.9, B.MushroomFields);
  set(0.05, B.SnowyBeach);
  set(0.2, B.StonyShore, B.WindsweptHills, B.WindsweptGravellyHills, B.WindsweptForest);
  set(2, B.Desert, B.Savanna, B.SavannaPlateau, B.WindsweptSavanna, B.Badlands, B.WoodedBadlands, B.ErodedBadlands);
  set(0.7, B.Forest, B.FlowerForest, B.DarkForest, B.PaleGarden);
  set(0.6, B.BirchForest, B.OldGrowthBirchForest);
  set(0.25, B.Taiga, B.OldGrowthSpruceTaiga);
  set(0.3, B.OldGrowthPineTaiga);
  set(-0.5, B.SnowyTaiga);
  set(0.95, B.Jungle, B.SparseJungle, B.BambooJungle);
  set(-0.2, B.Grove);
  set(-0.3, B.SnowySlopes);
  set(-0.7, B.JaggedPeaks, B.FrozenPeaks);
  set(1, B.StonyPeaks);
  return t;
})();

/** Biomes with has_precipitation = false: no rain or snow at all. */
const DRY = new Set<Biome>([
  B.Desert, B.Savanna, B.SavannaPlateau, B.WindsweptSavanna, B.Badlands, B.WoodedBadlands, B.ErodedBadlands,
]);

export const PRECIP_NONE = 0;
export const PRECIP_RAIN = 1;
export const PRECIP_SNOW = 2;
export type Precip = 0 | 1 | 2;

/** Vanilla: snow when the temperature, lowered 0.00125 per block above y 80, is under 0.15. */
export const SNOW_TEMP = 0.15;
const LAPSE_Y = 80;
const LAPSE = 0.00125;

/** Temperature of a biome at height y (vanilla Biome.getTemperature, without the noise patches). */
export function temperatureAt(biome: Biome, y: number): number {
  const t = BIOME_TEMP[biome] ?? 0.8;
  return y > LAPSE_Y ? t - (y - LAPSE_Y) * LAPSE : t;
}

/** Lowest y at which `biome` snows instead of raining (Infinity when it never does). */
export function snowLineY(biome: Biome): number {
  const t = BIOME_TEMP[biome] ?? 0.8;
  if (t < SNOW_TEMP) return -Infinity;
  return LAPSE_Y + (t - SNOW_TEMP) / LAPSE;
}

/** What falls out of the sky at block height y: nothing (dry biome), rain or snow. */
export function precipitationAt(biome: Biome, y: number): Precip {
  if (DRY.has(biome)) return PRECIP_NONE;
  return temperatureAt(biome, y) < SNOW_TEMP ? PRECIP_SNOW : PRECIP_RAIN;
}

/** Could water freeze / snow settle at this biome and height? (same cold test, ignoring dryness) */
export function isColdAt(biome: Biome, y: number): boolean {
  return temperatureAt(biome, y) < SNOW_TEMP;
}

// --- sky / fog / water fog ---------------------------------------------------

/** Vanilla fog colour of almost every overworld biome. */
export const DEFAULT_FOG = 0xc0d8ff;
/** Vanilla default underwater fog colour (cold / frozen oceans, rivers, land). */
export const DEFAULT_WATER_FOG = 0x050533;

function hsvToRgb(h: number, s: number, v: number): number {
  const i = Math.floor(h * 6), f = h * 6 - i;
  const p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s);
  const [r, g, b] = [[v, t, p], [q, v, p], [p, v, t], [p, q, v], [t, p, v], [v, p, q]][((i % 6) + 6) % 6];
  return (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255);
}

/** Vanilla Biome.calculateSkyColor: a hue/saturation shift of blue by temperature. Plains = 0x78a7ff. */
export function skyColorOfTemp(temp: number): number {
  const t = Math.max(-1, Math.min(1, temp / 3));
  return hsvToRgb(0.62222224 - t * 0.05, 0.5 + t * 0.1, 1);
}

/** Overworld sky colour (0xRRGGBB, sRGB) of a biome. */
export function biomeSkyColor(biome: Biome): number {
  return skyColorOfTemp(BIOME_TEMP[biome] ?? 0.8);
}

/** Overworld fog colour (0xRRGGBB, sRGB) of a biome; vanilla's pale garden is the grey exception. */
export function biomeFogColor(biome: Biome): number {
  return biome === B.PaleGarden ? 0x817770 : DEFAULT_FOG;
}

/** Underwater fog colour (0xRRGGBB, sRGB) of a biome (vanilla water_fog_color). */
export function biomeWaterFog(biome: Biome): number {
  switch (biome) {
    case B.WarmOcean: return 0x041f33;
    case B.LukewarmOcean: case B.DeepLukewarmOcean: return 0x041633;
    case B.Swamp: return 0x232317;
    case B.MangroveSwamp: return 0x4d7a60;
    default: return DEFAULT_WATER_FOG;
  }
}

/** Vanilla BiomeTags.HAS_CLOSER_WATER_FOG: underwater view distance x0.85. */
export function hasCloserWaterFog(biome: Biome): boolean {
  return biome === B.Swamp || biome === B.MangroveSwamp;
}

// --- Nether ------------------------------------------------------------------

export type AmbientKind = 'none' | 'ash' | 'white_ash' | 'crimson_spore' | 'warped_spore';

export interface NetherBiomeAtmosphere {
  /** Vanilla fog_color (sRGB). */
  fog: number;
  /** Ambient particle and its spawn rate near the camera, particles per second
   *  (vanilla spawns far more; this keeps a phone's particle budget). */
  particle: AmbientKind;
  rate: number;
}

export const NETHER_ATMOSPHERE: Record<NetherBiome, NetherBiomeAtmosphere> = {
  [NetherBiome.Wastes]: { fog: 0x330808, particle: 'none', rate: 0 },
  [NetherBiome.SoulSandValley]: { fog: 0x1b4745, particle: 'ash', rate: 18 },
  [NetherBiome.CrimsonForest]: { fog: 0x330303, particle: 'crimson_spore', rate: 40 },
  [NetherBiome.WarpedForest]: { fog: 0x1a051a, particle: 'warped_spore', rate: 26 },
  [NetherBiome.BasaltDeltas]: { fog: 0x685f70, particle: 'white_ash', rate: 70 },
};

/** Nether ambient particle sprite colour (sRGB 0..1) per kind. */
export const AMBIENT_COLOR: Record<AmbientKind, readonly [number, number, number]> = {
  none: [1, 1, 1],
  ash: [0.28, 0.28, 0.3],
  white_ash: [0.78, 0.76, 0.78],
  crimson_spore: [0.85, 0.16, 0.22],
  warped_spore: [0.14, 0.78, 0.66],
};
